<template>

    <v-form ref="form" :disabled="props.disabled" :readonly="readonlyFields">
        <v-alert
            v-if="props.standalone && alertInfo.type && alertInfo.type !== 'error'"
            :color="alertInfo?.type ?? 'info'"
            :title="alertInfo.title"
            :text="alertInfo.text"
            closable
            :variant="alertInfo?.variant ?? 'tonal'"
            class="mb-4"
        />
        <v-row class=" ma-0">
            <v-col class="pa-0" cols="12">
                <v-autocomplete 
                    v-model="formValues.type" 
                    label="Tipo de Avaliação" 
                    :items="assessmentTypesList" 
                    item-value="value" 
                    item-title="title" 
                    autocomplete="off" 
                    :clearable="!readonlyFields && !props.assessmentType"
                    :disabled="props.disabled"
                    :menu-icon="(readonlyFields || !!props.assessmentType) ? '' : undefined"
                    :readonly="readonlyFields || !!props.assessmentType"
                    :rules="[
                        formValidationRules.requiredIf(
                            formValues.type,
                            !readonlyFields
                        ),
                    ]"
                />
            </v-col>
        </v-row>
        <v-row class="row ma-0">
            <v-col class="pa-0" cols="12">
                <v-autocomplete 
                    v-model="formValues.year" 
                    label="Ano da avaliação" 
                    :items="props.yearsList"
                    autocomplete="off"
                    :clearable="!readonlyFields"
                    :loading="props?.isFetchingYearsList"
                    :disabled="props?.isFetchingYearsList"
                    :rules="[
                        formValidationRules.requiredIf(
                            formValues.year,
                            !readonlyFields
                        ),
                    ]"
                />
            </v-col>
        </v-row>
        <!-- <v-row v-if="props.bulkCreate" class="ma-0">
            <v-col class="pa-0" cols="12">
                <div 
                    class="d-flex align-start flex-column"
                    
                >
                    <p>Criar avaliações por:</p>
                    <v-switch
                        v-model="formValues.mode"
                        false-value="departments"
                        true-value="users"
                        inset
                        :indeterminate="formValues.mode === null"
                    >
                        <template #prepend>
                            <span
                                class="cursor-pointer"
                                @click="formValues.mode = 'departments'"
                            >
                                Centros de Custo
                            </span>
                        </template>

                        <template #append>
                            <span
                                class="cursor-pointer"
                                @click="formValues.mode = 'users'"
                            >
                                Colaboradores
                            </span>
                        </template>
                    </v-switch>
                </div>
            </v-col>
        </v-row> -->
        <v-row v-if="props.bulkCreate" class="ma-0">
            <v-col class="pa-0" cols="12">
                <v-autocomplete 
                    v-model="formValues.department_ids" 
                    label="Centros de Custo" 
                    :items="departmentsList" 
                    item-value="id" 
                    :item-title="(item) => `${item?.external_key ?? 'CC sem código'} - ${item?.name ?? 'CC sem nome'}`" 
                    :loading="isLoadingDepartmentsList" 
                    :disabled="isLoadingDepartmentsList" 
                    autocomplete="off" 
                    :clearable="!readonlyFields"
                    multiple
                    :rules="[
                        formValidationRules.requiredIfAll(
                            formValues.type,
                            [
                                props.bulkCreate,
                                !readonlyFields,
                                formValidationRules.isEmpty(formValues.user_ids),
                            ]
                        ),
                    ]"
                />
            </v-col>
        </v-row>
        <v-row  v-if="props.bulkCreate || formValues.type === 'downward'" class="row ma-0">
            <v-col class="pa-0" cols="12">
                <v-autocomplete 
                    v-model="formValues.user_ids" 
                    label="Colaborador(a) a avaliar" 
                    :items="usersList" 
                    item-value="id" 
                    :multiple="props.bulkCreate" 
                    :loading="resolvedIsLoadingUsersList" 
                    :disabled="resolvedIsLoadingUsersList || isSubmitting" 
                    :item-title="(item) => `${item?.worker_code ? item.worker_code : 'Colaborador(a) sem código'} - ${item?.name ? item.name : 'Colaborador(a) sem nome'}`" 
                    autocomplete="off"
                    :clearable="!readonlyFields"
                    :hint="
                        isLoadingUsersList && !readonlyFields
                        ? `Carregando lista de colaboradores ${userListPagination?.page || 1}/${userListPagination.last_page ?? '??'}... `
                        : `Lista de colaboradores carregada: ${userListPagination.page ?? 1}/${userListPagination.last_page ?? 1}.`
                    "
                    :persistent-hint="!readonlyFields"
                    :rules="[
                        formValidationRules.requiredIfAll(
                            formValues.type,
                            [
                                props.bulkCreate,
                                !readonlyFields,
                                formValidationRules.isEmpty(formValues.department_ids),
                            ]
                        ),
                    ]"
                />
            </v-col>
        </v-row>
        <v-alert
            v-if="props.standalone && alertInfo.type && alertInfo.type === 'error'"
            :color="'error'"
            :title="alertInfo.title"
            :text="alertInfo.text"
            closable
            variant="tonal"
            class="mb-4"
        />
        <v-row v-if="props.standalone" class="ma-0 d-flex justify-end">
            <v-col class="pa-0" cols="12" md="auto">
                <v-btn
                    :prepend-icon="
                        isSubmitting
                        ? 'mdi-loading  mdi-spin'
                        : 'mdi-plus'
                    "
                    color="success"
                    variant="flat"
                    :disabled="isSubmitting"
                    @click="submit"
                >
                    {{ resolvedSubmitButtonLabel }}
                </v-btn>
            </v-col>
        </v-row>
    </v-form>

</template>
<script setup>
import { computed, onMounted, ref } from 'vue';
import User from "@/api/User.js";
import Department from "@/api/departments/Department.js";
import SelfAssessmentQuestionaires from "@/api/assessments/questionaires/SelfAssessmentQuestionaires.js";
import AscendingAssessmentQuestionaires from "@/api/assessments/questionaires/AscendingAssessmentQuestionaires.js";
import DownwardAssessmentQuestionaires from "@/api/assessments/questionaires/DownwardAssessmentQuestionaires.js";
import formValidationRules from "@/composables/formValidationRules";

const props = defineProps({
    disabled: {
        type: Boolean,
        default: false,
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    assessmentType: {
        type: String,
        default: null,
        validator: (value, props) => {
            return [null, "self", "ascending", "downward"].includes(value);
        },
    },
    assessmentId: {
        type: String,
        default: null,
    },
    startingYear : {
        type: [String, Number],
        default: 2000,
    },
    yearsList : {
        type: Array,
        default(rawProps) {
            return Array.from(
                Array(new Date().getFullYear() - rawProps.startingYear + 1).keys(),
                (v, i) => i + rawProps.startingYear
            ).reverse()
        }
    },
    isFetchingYearsList: {
        type: Boolean,
        default: false,
    },
    usersList : {
        type: Array,
        default: Array(),
    },
    isLoadingUsersList: {
        type: Boolean,
        default: false,
    },
    standalone: {
        type: Boolean,
        default: false,
    },
    bulkCreate: {
        type: Boolean,
        default: false,
    },
});

defineExpose( {
    validate,
    submit,
});

const assessmentTypesList = [
    {
        title: "Auto-Avaliações",
        value: "self"
    },
    {
        title: "Ascendente",
        value: "ascending",
    },
    {
        title: "Descendente",
        value: "downward"
    }
];

const paginationTemplate = {
    from: null,
    to: null,
    last_page: null,
    per_page: 50,
    total: null,
    page: 0,
};

const isLoadingUsersList = ref(false);
const resolvedIsLoadingUsersList = computed(() => {
    return props.isLoadingUsersList 
            || isLoadingUsersList.value;
})
const isLoadingDepartmentsList = ref(false);
const form = ref(null);
const localUsersList = ref([]);
const usersList = computed(() => {
    return props.usersList?.length
        ? props.usersList
        : localUsersList.value;
});

const alertInfo = ref({
    type: null,
    title: null,
    text: null,
});


const isSubmitting = ref(false);

const departmentsList = ref([]);

const formValues = ref({
    type: props.assessmentType ?? null,
    year: null,
    // mode: null,
    department_ids: [],
    user_ids: []
});

const userListPagination = ref({...paginationTemplate});

const departmentListPagination = ref({...paginationTemplate});


const resolvedAssessmentApi = computed(() => {
    switch (formValues.value.type ?? props.assessmentType) {
        case "self":
            return SelfAssessmentQuestionaires;
        case "ascending":
            return AscendingAssessmentQuestionaires;
        case "downward":
            return DownwardAssessmentQuestionaires;
    
        default:
            return null;
    }
}) ;

const resolvedAssessmentTypeLabel = computed(() => {
    let label = "";
    let evaluationTypeLabel;

    if(props.bulkCreate) {
        label = "Avaliações"
    } else {
        label = "Avaliação"
    }

    switch (formValues.value.type) {
        case "self":
            evaluationTypeLabel = `Auto-${label}`
            break;
    
        case "ascending":
            evaluationTypeLabel = `${label} Ascendente${props.bulkCreate ? 's' : ''}`
            break;
    
        case "downward":
            evaluationTypeLabel = `${label} Descendente${props.bulkCreate ? 's' : ''}`
            break;
    
        default:
            evaluationTypeLabel = label;
            break;
    }

    return evaluationTypeLabel;
})


const resolvedSubmitButtonLabel = computed(() => {

    let verbLabel;
    
    
    if(!isSubmitting.value) {
        verbLabel = "Criar";
    } else {
        verbLabel = "Criando";
    }

    return `${verbLabel} ${resolvedAssessmentTypeLabel.value}${isSubmitting.value ? '...' : ''}`;
});


const readonlyFields = computed(() => {
    return props.readonly;
});

async function resolveSubjectUser(user) {
    if(!user) return;

    if(typeof user === "string") {
        const response = await User.find(user);

        return response?.data
    }

    return user;
}

async function fetchUsers() {
    if(resolvedIsLoadingUsersList.value) {
        return;
    }

    isLoadingUsersList.value = true;

    try {
        do {
            
            const nextPage = userListPagination.value.page + 1;
            const response = await User.list({
                without_pending_membership_requests: 0,
            });
            const responseData = response?.data;
            const isPaginated = responseData?.data && responseData?.current_page;
            const newUsers = responseData?.data ?? responseData ?? [];

            localUsersList.value = [
                ...localUsersList.value,
                ...newUsers,
            ];

            if (!isPaginated) {
                break;
            }

            userListPagination.value = {
                ...userListPagination.value,
                page: responseData.current_page,
                last_page: responseData.last_page,
                from: responseData.from,
                to: responseData.to,
                total: responseData.total,
                per_page: responseData.per_page,
            };

            if(isInitialLoadingUsersList.value) {
                isInitialLoadingUsersList.value = false;
            }
        } while (userListPagination.value.page < userListPagination.value.last_page);
    } catch (error) {
        console.error(error);
    } finally {
        isLoadingUsersList.value = false;
    }
}

async function fetchDepartments() {
    isLoadingDepartmentsList.value = true;

    try {
        const response = await Department.all({ request_domain: "assessments" });

        departmentsList.value = response?.data?.data
                                ?? response?.data
                                ?? [];
    } catch (error) {
        // departments.value = [];
        console.error("Não foi possível carregar os centros de custo:", error);

        alertInfo.value = {
            type: "error",
            title: `${error?.response?.statusText ?? error?.code ?? 'Erro'}`,
            text: `${error?.response?.data?.message ?? error?.message}${error?.options?.cause ? ' - ' + error?.options?.cause : ''}`,
        }
        throw error;
    } finally {
        isLoadingDepartmentsList.value = false;
    }
}

async function validate() {

    const result = await form.value?.validate();

    return result?.valid ?? false;
}

async function submit() {
    if (isSubmitting.value) {
        return;
    }

    alertInfo.value = {
        type: null,
        title: null,
        text: null,
    };

    isSubmitting.value = true;

    try {
        const payload = {...formValues.value};
        if(!await validate()) {
            let failedValidationMessage = "Falhou a validação do formulário. Verifique as mensagens de erro em cada campo.";
            alertInfo.value = {
                type: "error",
                title: "Erro de Validação",
                text: failedValidationMessage,
            };
            return;
        }

        
        let response;
        let evaluationTypeLabel;
        if(
            props.bulkCreate
            && (
                payload?.user_ids?.length 
                || payload?.department_ids?.length) 
                
        ) {
            
            response = await resolvedAssessmentApi.value.bulkCreate(payload)
            
            alertInfo.value = {
                type: "success",
                title: "Sucesso",
                text: `Foram geradas com sucesso as ${resolvedAssessmentTypeLabel.value} de ${payload.year} para: ${response?.data?.map((assessment) => {
                    switch (payload.type) {
                        case "self":
                            
                            return assessment?.user?.name 
                                    ?? assessment?.user?.id 
                                    ?? assessment?.user_id;

                        case "ascending":
                            
                            return assessment?.subject?.name 
                                    ?? assessment?.subject?.id 
                                    ?? assessment?.subject_user_id;

                        case "downward":
                            
                            return assessment?.subjectUser?.name 
                                    ?? assessment?.subject_user?.name 
                                    ?? assessment?.subjectUser?.id 
                                    ?? assessment?.subject_user?.id
                                    ?? assessment?.subject_user_id;
                    
                        default:
                            break;
                    }
                })?.filter(Boolean)?.join("; ")}.`,
                variant: "flat",
            }
        }
        else {
            if(formValues.value.type === 'downward') {
                payload.subject_user_id = payload.user_ids;

                delete payload.user_ids;
            }
            response = await resolvedAssessmentApi.value.create(payload);
            let employeeName;

            switch (payload.type) {
                case "self":
                    employeeName = response?.data?.user?.name
                                    ?? response?.data?.user?.id
                                    ?? response?.data?.user_id
                    ;
                    break;
            
                case "ascending":
                    employeeName = response?.data?.subject?.name
                                    ?? response?.data?.subject?.id
                                    ?? response?.data?.subject_user_id
                    ;
                    break;
            
                case "downward":
                    employeeName = response?.data?.subject_user?.name
                                    ?? response?.data?.subject_user?.id
                                    ?? response?.data?.subject_user_id
                    
                    ;
                    break;
            
                default:
                    break;
            }

            alertInfo.value = {
                type: "success",
                title: "Sucesso",
                text: `Foi gerada a sua ${resolvedAssessmentTypeLabel.value} relativa a ${payload.year}${employeeName ? ' para ' + employeeName : ''}.`,
                variant: "flat",
            }
        }
        
        console.log(response);
    } catch (error) {
        alertInfo.value = {
            type: "error",
            title: `${error?.response?.statusText ?? error?.code ?? 'Erro'}`,
            text: `${error?.response?.data?.message ?? error?.message}${error?.options?.cause ? ' - ' + error?.options?.cause : ''}`,
        }
        throw error;
    } finally {
        isSubmitting.value = false;
    }
}



onMounted(() => {
    if(!props.usersList?.length && !props.isLoadingUsersList) {
        fetchUsers();
    }
    fetchDepartments();
})
</script>
